import { AdminImportCourse, AdminImportStudent, AdminImportTeacher } from "../rosterImportApiSlice";

export type PhaseKey = "courses" | "teachers" | "students";

export type ImportEntity = AdminImportStudent | AdminImportTeacher | AdminImportCourse;

export type ImportRow = {
	key: PhaseKey;
	phase: string;
	inserts: number;
	updates: number;
	noChanges: number;
	issues: number;
	errorMessages: string[];
};

type Item = {
	key: string;
	rowNum: number | null;
	entity?: ImportEntity;
	messages: string[];
};

export type ErrorCategoryKey =
	| "registration"
	| "username"
	| "password"
	| "courseGrade"
	| "courseName"
	| "other";

export type PhaseCategory = { key: ErrorCategoryKey; label: string };

export const PHASE_CATEGORIES: Record<PhaseKey, PhaseCategory[]> = {
	students: [
		{ key: "registration", label: "Registration" },
		{ key: "username", label: "Username" },
		{ key: "password", label: "Password" },
		{ key: "other", label: "Other" },
	],
	teachers: [
		{ key: "registration", label: "Registration" },
		{ key: "username", label: "Username" },
		{ key: "password", label: "Password" },
		{ key: "other", label: "Other" },
	],
	courses: [
		{ key: "courseGrade", label: "Grade" },
		{ key: "courseName", label: "Name" },
		{ key: "other", label: "Other" },
	],
};

const PATTERNS = {
	rowNumber: /\bRow\s+(\d+)\b/i,
	rowPrefix: /^\s*Row\s+\d+:\s*/i,

	usernameTaken: /username\s+'.*'\s+is\s+already\s+taken/i,
	userNameCreationFailed: /user\s*name\s+creation\s+failed/i,
	passwordCreationFailed: /password\s+creation\s+failed/i,
	registrationFailed: /\bregistration\s+to\b.*\bfailed\b/i,

	schoolDoesntExist: /school\s+doesn'?t\s+exist/i,

	courseInvalidGrade: /course\s+has\s+invalid\s+grade/i,
	courseNameNotUnique:
		/more\s+than\s+one\s+course\s+with\s+this\s+name|course\s+name\s+is\s+not\s+unique/i,
	courseDoesNotExist: /course\s+does\s+not\s+exist/i,
} as const;

export const stripRowPrefix = (msg: string) => msg.replace(PATTERNS.rowPrefix, "").trim();

const normalize = (rawMsg: string) => stripRowPrefix(rawMsg).toLowerCase();

// #region Build and Categorize Items For PhaseIssuesPanel
const parseRowNumber = (msg: string): number | null => {
	const m = msg.match(PATTERNS.rowNumber);
	return m ? Number(m[1]) : null;
};

export function buildItemsForCategory(
	errorMessages: string[],
	entitiesByRow: Map<number, ImportEntity>
): Item[] {
	const itemsByKey = new Map<string, Item>();

	for (const msg of errorMessages) {
		const rowNum = parseRowNumber(msg);
		const key = rowNum == null ? "general" : `row:${rowNum}`;

		//make a new item if it doesn't already exist
		if (!itemsByKey.has(key)) {
			itemsByKey.set(key, {
				key,
				rowNum,
				entity: rowNum != null ? entitiesByRow.get(rowNum) : undefined,
				messages: [],
			});
		}

		//add the message to the item by the key
		itemsByKey.get(key)!.messages.push(msg);
	}

	//sort the items by row number
	const items = Array.from(itemsByKey.values()).sort((a, b) => {
		if (a.rowNum == null && b.rowNum == null) return 0;
		if (a.rowNum == null) return 1;
		if (b.rowNum == null) return -1;
		return a.rowNum - b.rowNum;
	});

	//sort the errors in each item
	for (const i of items) {
		i.messages.sort((a, b) => a.localeCompare(b));
	}
	return items;
}

export function categorizeError(phaseKey: PhaseKey, rawMsg: string): ErrorCategoryKey {
	const s = normalize(rawMsg);

	// school/system grouped into "other"
	if (PATTERNS.schoolDoesntExist.test(s)) return "other";

	if (phaseKey === "students" || phaseKey === "teachers") {
		if (
			PATTERNS.userNameCreationFailed.test(s) ||
			PATTERNS.usernameTaken.test(s) ||
			s.includes("username")
		)
			return "username";

		if (PATTERNS.passwordCreationFailed.test(s) || s.includes("password")) return "password";

		if (PATTERNS.registrationFailed.test(s) || s.includes("registration"))
			return "registration";

		return "other";
	}

	if (phaseKey === "courses") {
		if (
			PATTERNS.courseInvalidGrade.test(s) ||
			s.includes("invalid grade") ||
			s.includes("grade")
		)
			return "courseGrade";

		if (
			PATTERNS.courseNameNotUnique.test(s) ||
			PATTERNS.courseDoesNotExist.test(s) ||
			s.includes("course name") ||
			s.includes("name")
		)
			return "courseName";

		return "other";
	}

	return "other";
}
// #endregion

// #region Registration blurb functions
const REG_LICENSE_BLURB =
	"Student can't be registered in the course. Either school license is no longer active or there are no available student licenses remaining.";

export const hasRegistrationLicenseBlurb = (msg: string) =>
	msg.toLowerCase().includes(REG_LICENSE_BLURB.toLowerCase());

const stripRegistrationLicenseBlurb = (msg: string) =>
	msg.replace(REG_LICENSE_BLURB, "").replace(/\s+/g, " ").trim();

export function formatIssueForList(raw: string, openCategory: ErrorCategoryKey | null) {
	let msg = stripRowPrefix(raw);

	if (openCategory === "registration" && hasRegistrationLicenseBlurb(msg)) {
		msg = stripRegistrationLicenseBlurb(msg);
	}

	return msg;
}
// #endregion
